import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { mapAttachment } from "./attachments";
import { api, API_BASE, ApiError } from "./client";
import type { ApiRoutineChange } from "./routines";
import type { ApiChatMessage, ApiToolCall, ApiToolCallUpdate } from "./types";
import { useResource } from "./useResource";
import { slug } from "../../lib/slug";
import { relTime } from "../../lib/time";
import type { Message, MessageRole, ToolCall } from "../types";

const toRole = (role: string): MessageRole => {
  if (role === "agent" || role === "system") {
    return role;
  }
  return "user";
};

const toolStatus = (s: string): ToolCall["status"] =>
  s === "running" || s === "failed" || s === "cancelled" || s === "succeeded"
    ? s
    : undefined;

const mapToolCall = (t: ApiToolCall): ToolCall => ({
  arg: t.argument_summary ?? "",
  body: t.body_preview ?? "",
  bodyLength: t.body_length ?? null,
  durationMs: t.duration_ms,
  images: (t.images ?? []).map((image) => ({
    bytes: image.bytes,
    mime: image.mime,
    publicId: image.public_id,
  })),
  name: t.name,
  publicId: t.public_id,
  status: toolStatus(t.status),
});

/**
 * Where a tool-call image's bytes live, for `<img>` thumbnails and the
 * pop-out's download. Served with `Content-Disposition: attachment` and
 * `nosniff` like an attachment's, which leaves image decoding unaffected
 * (`api.md` §Chat).
 */
export const toolCallImagePath = (publicId: string): string =>
  `${API_BASE}/tool-call-images/${publicId}/content`;

/**
 * What a tool's screenshot is saved as. Nothing named these bytes — they came
 * off a tool result, not out of a file picker — so the tool that returned them
 * names them, and the MIME subtype supplies the extension. Only the four
 * raster types are ever stored (`chat.md` §Tool calls that return an image),
 * so the subtype is always the extension; a browser numbers the collisions
 * when one turn returns several.
 *
 * The label is a heading and not a filename — `chrome-devtools: take
 * screenshot` — so it goes through the same slug the task sub-element URLs
 * use. A colon is the character that matters: Windows refuses to write it.
 */
export const toolCallImageFilename = (label: string, mime: string): string =>
  `${slug(label) || "image"}.${mime.split("/")[1] ?? "png"}`;

// The row's timestamp as the thread renders it. Relative to now, and so the
// one part of the mapping below that goes stale on its own — which is why the
// cache in `useChatMessages` has to be able to ask for it without rebuilding
// the whole message.
const relativeTimestamp = (row: ApiChatMessage): string =>
  relTime(row.occurred_at ? Date.parse(row.occurred_at) : Date.now());

// A task message is named as the agent that wrote it, never as the owner it is
// authored under (task-messages.md §In the threads).
const userName = (
  row: ApiChatMessage,
  fromTask: Message["fromTask"],
): string =>
  fromTask ? `Agent ${fromTask.label}` : (row.display_name ?? "Unknown user");

// Map a persisted chat row into the chat view model. System rows render as a
// system event (title + steps); user/agent rows use `text` and may carry the
// turn's tool calls (chat.md §Storage).
//
// A `tool_use` row is the tool-call card and nothing else: its body is dropped
// so the card's own header carries the tool name (chat.md §Tool use). The
// server no longer writes one, but transcripts recorded before that still hold
// a "Running <name>…" line, and this keeps them quiet too.
export const mapMessage = (row: ApiChatMessage): Message => {
  const role = toRole(row.role);
  const tools = (row.tool_calls ?? []).map(mapToolCall);
  const toolUse = row.sdk_message_kind === "tool_use";
  const attachments = (row.attachments ?? []).map(mapAttachment);
  const fromTask = row.from_task ? { label: row.from_task.label } : undefined;
  return {
    attachments: attachments.length > 0 ? attachments : undefined,
    avatarInitials:
      role === "user" && !fromTask
        ? (row.avatar_initials ?? undefined)
        : undefined,
    fromTask,
    id: row.public_id,
    role,
    sequence: row.sequence,
    steps: role === "system" ? (row.system_event?.steps ?? []) : undefined,
    systemKind:
      role === "system" ? (row.system_event?.kind ?? undefined) : undefined,
    text: role === "system" || toolUse ? undefined : (row.body ?? ""),
    thinking: row.thinking,
    title:
      role === "system"
        ? (row.system_event?.title ?? row.body ?? "")
        : undefined,
    tools: tools.length > 0 ? tools : undefined,
    ts: relativeTimestamp(row),
    who: role === "user" ? userName(row, fromTask) : undefined,
  };
};

type MessagesEnvelope = {
  readonly messages: ReadonlyArray<ApiChatMessage>;
  readonly next_after_seq: number | null;
};

const PAGE = 100;

// Union two ascending-by-sequence lists, de-duped by the monotonic per-task
// `sequence`, kept sorted. Used to prepend older pages and append newer ones.
const mergeRows = (
  a: ReadonlyArray<ApiChatMessage>,
  b: ReadonlyArray<ApiChatMessage>,
): ReadonlyArray<ApiChatMessage> => {
  const bySeq = new Map<number, ApiChatMessage>();
  for (const m of a) {
    bySeq.set(m.sequence, m);
  }
  for (const m of b) {
    bySeq.set(m.sequence, m);
  }
  return [...bySeq.values()].sort((x, y) => x.sequence - y.sequence);
};

// The row a tool call hangs off, as the server wrote it: an agent `tool_use`
// message with no body of its own (chat.md §Tool use). Synthesised when a call
// opens on a turn newer than anything the window holds, so the card appears as
// the agent makes it rather than at the next refetch. A later read of the same
// `sequence` replaces it with the persisted row, which renders identically.
const toolUseRow = (update: ApiToolCallUpdate): ApiChatMessage => ({
  author_public_id: null,
  author_user_id: null,
  avatar_initials: null,
  body: null,
  body_format: "markdown",
  display_name: null,
  occurred_at: new Date().toISOString(),
  public_id: update.message_public_id,
  role: "agent",
  sdk_message_kind: "tool_use",
  sequence: update.message_sequence,
  thinking: false,
  tool_calls: [update],
});

// Apply one `chat.tool_call.updated` to the loaded window.
//
// The event carries the whole card, not a cue to refetch: a tool-heavy turn is
// a card every few seconds, and re-reading the page for each one is both
// wasteful and too late — the transition a reader watches for (running →
// succeeded) is a rewrite of a row that sits *behind* the paging cursor for the
// rest of its life, so `refetch` would never see it (realtime.md §Recovering a
// missed rewrite).
export const applyToolCall = (
  rows: ReadonlyArray<ApiChatMessage>,
  update: ApiToolCallUpdate,
): ReadonlyArray<ApiChatMessage> => {
  const parent = rows.find((r) => r.public_id === update.message_public_id);
  if (!parent) {
    const newest = rows[rows.length - 1];
    // A row older than the window is one we are not showing; it will arrive
    // with the call already on it if the reader ever pages back to it. With no
    // window at all the first page is still in flight, and it is the one to
    // wait for — a lone card is not a transcript.
    return newest && update.message_sequence > newest.sequence
      ? [...rows, toolUseRow(update)]
      : rows;
  }
  const calls = parent.tool_calls ?? [];
  const known = calls.some((c) => c.public_id === update.public_id);
  return rows.map((row) =>
    row === parent
      ? {
          ...row,
          tool_calls: known
            ? calls.map((c) => (c.public_id === update.public_id ? update : c))
            : [...calls, update],
        }
      : row,
  );
};

export type UseChatMessages = {
  // Apply a live tool-call transition to the window, in place and without a
  // round trip (see `applyToolCall`).
  readonly applyToolCall: (update: ApiToolCallUpdate) => void;
  readonly error: ApiError | undefined;
  // More history exists above the currently-loaded window (chat.md §Pagination).
  readonly hasMore: boolean;
  readonly loading: boolean;
  readonly loadingOlder: boolean;
  readonly loadOlder: () => void;
  readonly messages: ReadonlyArray<Message>;
  // The highest `sequence` in the window, or 0 while it is empty. The caret
  // reads it to know when the row it has been streaming has landed
  // (`realtime.md` §Caret rules).
  readonly newestSequence: number;
  // Pull anything appended after the newest row we hold.
  readonly refetch: () => void;
  // Re-read the newest page over the rows we already hold. For a message that
  // *changed* rather than one that was appended — the boot card is rewritten in
  // place as provisioning walks its steps — where `refetch` asks for rows after
  // the very sequence that moved and correctly gets nothing back.
  readonly reload: () => void;
};

// Paged chat transcript: loads the latest page, pages older via `before_seq` on
// scroll-up, and appends newer rows via `after_seq` when a realtime event fires
// (chat.md §Pagination). Accumulating (rather than re-windowing) keeps older
// pages the reader scrolled to from vanishing on the next message.
export const useChatMessages = (taskId: string | null): UseChatMessages => {
  const [rows, setRows] = useState<ReadonlyArray<ApiChatMessage>>([]);
  const [loading, setLoading] = useState(taskId !== null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<ApiError | undefined>(undefined);

  // `reload` and `refetch` capture the task they were built for, and their
  // responses can land in any order relative to the first page. Two rules
  // follow, both recorded in a ref because they gate a `setRows` rather than
  // being rendered:
  //
  //  - a response for a task the view has left is dropped, not written into the
  //    next task's window;
  //  - once a *newer* window has been applied, the first page must not
  //    overwrite it. That page is issued on mount and is still in flight while
  //    provisioning walks its steps, so the `chat.message.updated` it races
  //    answers with a fresher card — and putting the mount-time snapshot back
  //    on top of it strands the provisioning card on the step it was on when
  //    the request went out. Nothing re-reads a row that changed in place
  //    afterwards (realtime.md §Messages that change).
  const applied = useRef<{ superseded: boolean; task: string | null }>({
    superseded: false,
    task: taskId,
  });

  // Initial (and on-task-change) load of the latest page.
  useEffect(() => {
    applied.current = { superseded: false, task: taskId };
    if (taskId === null) {
      setRows([]);
      setLoading(false);
      setHasMore(false);
      return;
    }
    let active = true;
    setLoading(true);
    api
      .get<MessagesEnvelope>(`/tasks/${taskId}/messages`, {
        query: { limit: PAGE },
      })
      .then((res) => {
        if (!active) {
          return;
        }
        // A newer window already landed while this page was in flight; it is
        // the one to keep (see `applied`). The page size still answers whether
        // there is history above it either way.
        if (!applied.current.superseded) {
          setRows(res.messages);
        }
        setHasMore(res.messages.length >= PAGE);
        setError(undefined);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!active) {
          return;
        }
        setError(
          err instanceof ApiError
            ? err
            : new ApiError("unknown", "Failed to load messages.", 0),
        );
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [taskId]);

  const loadOlder = useCallback((): void => {
    const oldest = rows[0];
    if (taskId === null || loadingOlder || !hasMore || !oldest) {
      return;
    }
    setLoadingOlder(true);
    api
      .get<MessagesEnvelope>(`/tasks/${taskId}/messages`, {
        query: { before_seq: oldest.sequence, limit: PAGE },
      })
      .then((res) => {
        setRows((cur) => mergeRows(res.messages, cur));
        setHasMore(res.messages.length >= PAGE);
      })
      .catch(() => undefined)
      .finally(() => setLoadingOlder(false));
  }, [taskId, loadingOlder, hasMore, rows]);

  // Pull newer messages after the newest we hold and merge them in. With no
  // rows yet (fresh mount racing a refetch) fall back to a latest-page load.
  const refetch = useCallback((): void => {
    if (taskId === null) {
      return;
    }
    const newest = rows[rows.length - 1];
    api
      .get<MessagesEnvelope>(`/tasks/${taskId}/messages`, {
        query: newest
          ? { after_seq: newest.sequence, limit: PAGE }
          : { limit: PAGE },
      })
      .then((res) => {
        if (applied.current.task !== taskId) {
          return;
        }
        applied.current.superseded = true;
        setRows((cur) =>
          newest ? mergeRows(cur, res.messages) : res.messages,
        );
      })
      .catch(() => undefined);
  }, [taskId, rows]);

  // Re-read the latest page and merge it over what we hold. `mergeRows` is
  // keyed on `sequence`, so a row that came back changed replaces the copy in
  // the window rather than appearing twice.
  const reload = useCallback((): void => {
    if (taskId === null) {
      return;
    }
    api
      .get<MessagesEnvelope>(`/tasks/${taskId}/messages`, {
        query: { limit: PAGE },
      })
      .then((res) => {
        if (applied.current.task !== taskId) {
          return;
        }
        applied.current.superseded = true;
        setRows((cur) => mergeRows(cur, res.messages));
      })
      .catch(() => undefined);
  }, [taskId]);

  const applyToolCallUpdate = useCallback((update: ApiToolCallUpdate): void => {
    setRows((cur) => applyToolCall(cur, update));
  }, []);

  // Mapped per row rather than per window, and cached on the row object.
  //
  // `mergeRows` and `applyToolCall` both preserve the identity of a row they
  // did not touch, so appending one message used to mint a whole window of new
  // `Message` objects for rows that had not changed — which defeated every
  // `memo` downstream and made one streamed token re-parse the markdown of
  // every message on screen. Keyed weakly, so a row that pages out of the
  // window takes its mapping with it.
  const mapped = useRef(new WeakMap<ApiChatMessage, Message>());
  const messages = useMemo(
    () =>
      rows.map((row) => {
        const hit = mapped.current.get(row);
        // Reused only while it still reads the same. `ts` is relative to now,
        // so a mapping made half an hour ago would say "just now" for ever —
        // and re-mapping the whole window is what used to keep it honest.
        // Checking costs a date parse; rebuilding costs the view model, and
        // then costs the render that no longer memoises.
        if (hit && hit.ts === relativeTimestamp(row)) {
          return hit;
        }
        const message = mapMessage(row);
        mapped.current.set(row, message);
        return message;
      }),
    [rows],
  );
  return {
    applyToolCall: applyToolCallUpdate,
    error,
    hasMore,
    loading,
    loadingOlder,
    loadOlder,
    messages,
    newestSequence: rows[rows.length - 1]?.sequence ?? 0,
    refetch,
    reload,
  };
};

// POST a user message. The server persists it, claims the named uploads onto
// it in the same transaction (chat.md §Composer), forwards to the sandbox
// daemon if one is attached, and returns the created row. A message needs a
// non-empty body OR at least one attachment; an attachment-only send omits
// `body` entirely.
export const sendChatMessage = (
  taskId: string,
  body: string,
  attachmentIds: ReadonlyArray<string> = [],
): Promise<unknown> =>
  api.post(`/tasks/${taskId}/messages`, {
    ...(body.trim() ? { body } : {}),
    ...(attachmentIds.length > 0 ? { attachment_ids: attachmentIds } : {}),
  });

export type RetriedTurn = {
  readonly retried: {
    readonly message_public_id: string;
    readonly prompt_public_id: string | null;
  };
};

// Re-run the agent's last turn: the server re-issues that turn's originating
// prompt to the daemon (chat.md §Copy and Retry). It appends nothing — the 202
// only says the prompt went back out, and the regenerated turn arrives as
// ordinary `chat.message.appended` rows — so there is nothing here to merge
// into the window.
export const retryAgentTurn = (
  taskId: string,
  messageId: string,
): Promise<RetriedTurn> =>
  api.post<RetriedTurn>(`/tasks/${taskId}/messages/${messageId}/retry`);

// The agent's pending clarifying question(s) (chat.md §inputRequired).
export type ClarifyingQuestion = {
  readonly header: string;
  readonly multiSelect: boolean;
  readonly options: ReadonlyArray<{
    readonly description: string;
    readonly label: string;
  }>;
  readonly question: string;
};

export type ApiInputRequest = {
  readonly public_id: string;
  readonly questions: ReadonlyArray<ClarifyingQuestion>;
  /** An agent's proposed routine change, which the server asks about. */
  readonly routine_change?: ApiRoutineChange | null;
  readonly status: string;
};

export type UseInputRequest = {
  readonly inputRequest: ApiInputRequest | null;
  readonly refetch: () => void;
};

export const useTaskInputRequest = (taskId: string | null): UseInputRequest => {
  const { data, refetch } = useResource<{
    input_request: ApiInputRequest | null;
  }>(taskId ? `/tasks/${taskId}/input-request` : null);
  return { inputRequest: data?.input_request ?? null, refetch };
};

// A slash command the agent session accepts — a skill, a project command, or
// one of the CLI's own built-ins (chat.md §Slash commands). `name` carries no
// leading slash; `argument_hint` is null when the command takes none.
export type ApiAgentCommand = {
  readonly aliases: ReadonlyArray<string>;
  readonly argument_hint: string | null;
  readonly description: string;
  readonly name: string;
};

export type UseAgentCommands = {
  readonly commands: ReadonlyArray<ApiAgentCommand>;
  readonly refetch: () => void;
};

// The commands the composer can complete. Empty for a task whose sandbox is
// still provisioning — the endpoint answers with a list either way, and the
// menu simply has nothing to offer until one is reported.
export const useAgentCommands = (taskId: string | null): UseAgentCommands => {
  const { data, refetch } = useResource<{
    commands: ReadonlyArray<ApiAgentCommand>;
  }>(taskId ? `/tasks/${taskId}/commands` : null);
  return { commands: data?.commands ?? [], refetch };
};

// Answer a pending input request. `answers` is keyed by question text →
// the chosen option label(s); `free_text` carries a typed reply instead.
export const answerInputRequest = (
  taskId: string,
  body: {
    answers?: Record<string, string>;
    free_text?: string | null;
    input_request_public_id: string;
  },
): Promise<unknown> => api.post(`/tasks/${taskId}/input-responses`, body);
